import { TYPE_EMOJI } from "../../lib/entry-text.ts";
import type { Baby } from "../../lib/queries.ts";
import { cn, perRow } from "../../lib/utils.ts";

/** Tabs to a row: four still fit a 320px phone, so more babies wrap to a second row. */
const MOST_TABS = 4;

/**
 * The twin switcher: ink-on-paper for the active tab, faded for the rest. A
 * sleeping baby's tab carries 😴, so you can see who's down without switching.
 * Past `MOST_TABS` babies the tabs wrap, and a short last row's tabs stretch
 * to fill it. The log and the overview on a phone share it.
 */
export function BabyTabs({
  babies,
  selectedId,
  asleep,
  onSelect,
  panelId,
  className,
}: {
  babies: Baby[];
  selectedId: string | null;
  /** Ids of babies with a nap in progress. */
  asleep: ReadonlySet<string>;
  onSelect: (id: string) => void;
  /** The id of the tab panel the tabs control, on a page that has one. */
  panelId?: string;
  className?: string;
}) {
  if (babies.length < 2) return null;
  // Rounded down, so a full row's tabs never add up to more than its width.
  const basis = `${Math.floor(10_000 / perRow(babies.length, MOST_TABS)) / 100}%`;
  return (
    <div
      role="tablist"
      aria-label="Baby"
      className={cn("mt-[18px] flex flex-wrap border border-divider", className)}
    >
      {babies.map((b) => {
        const active = b.id === selectedId;
        return (
          <button
            key={b.id}
            type="button"
            role="tab"
            id={panelId ? `${panelId}-${b.id}` : undefined}
            aria-selected={active}
            aria-controls={panelId}
            onClick={() => onSelect(b.id)}
            style={{ flexBasis: basis }}
            className={cn(
              "heading flex min-h-[52px] min-w-0 grow items-center justify-center px-2 text-[20px] transition-colors",
              active ? "bg-ink text-paper" : "bg-transparent text-ink opacity-55 hover:opacity-80",
            )}
          >
            {/* Only the name truncates, so a long name never hides the 😴. */}
            <span className="min-w-0 truncate">{b.name}</span>
            {asleep.has(b.id) ? (
              <>
                <span aria-hidden="true" className="ml-1.5 shrink-0 text-[17px]">
                  {TYPE_EMOJI.sleep}
                </span>
                <span className="sr-only"> (asleep)</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
